import { useState, useEffect } from 'react';
import { useQuery, useMutation, useQueryClient, keepPreviousData } from '@tanstack/react-query';
import { salesService } from '../services/sales.service';
import { 
  SalesOrder, 
  CreateSalesOrderRequest, 
  SalesOrderFilters, 
  SalesOrderStatus,
  SalesDashboardRange,
  SalesOrderQueryParams
} from '../types/sales.types';

export const useSalesOrdersPage = (params: SalesOrderQueryParams) => {
  return useQuery({
    queryKey: ['salesOrders', 'page', params],
    queryFn: () => salesService.getPage(params),
    placeholderData: keepPreviousData,
  });
};

export const useSalesOrderSummary = () => {
  return useQuery({
    queryKey: ['salesOrders', 'summary'],
    queryFn: () => salesService.getSummary(),
  });
};

export const useCreateSalesOrder = () => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: (data: CreateSalesOrderRequest) => salesService.create(data),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['salesOrders'] });
    },
  });
};

export const useUpdateSalesOrderStatus = () => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: ({ id, status }: { id: string; status: SalesOrderStatus }) =>
      salesService.updateStatus(id, status),
    onSuccess: (_data, variables) => {
      queryClient.invalidateQueries({ queryKey: ['salesOrders'] });
      // Confirming deducts stock; cancelling a confirmed order restores it
      if (variables.status === SalesOrderStatus.CONFIRMED || variables.status === SalesOrderStatus.CANCELLED) {
        queryClient.invalidateQueries({ queryKey: ['products'] });
      }
    },
  });
};

export const useSalesOrders = (filters?: SalesOrderFilters) => {
  const [orders, setOrders] = useState<SalesOrder[]>([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const fetchOrders = async () => {
    setLoading(true);
    setError(null);
    try {
      const data = await salesService.getAll(filters);
      setOrders(data);
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to fetch sales orders');
    } finally {
      setLoading(false);
    }
  };

  const createOrder = async (data: CreateSalesOrderRequest): Promise<SalesOrder | null> => {
    setError(null);
    try {
      const newOrder = await salesService.create(data);
      setOrders(prev => [...prev, newOrder]);
      return newOrder;
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to create sales order');
      return null;
    }
  };

  const updateOrderStatus = async (id: string, status: SalesOrderStatus): Promise<SalesOrder | null> => {
    setError(null);
    try {
      const updatedOrder = await salesService.updateStatus(id, status);
      setOrders(prev => prev.map(order => 
        order.id === id ? updatedOrder : order
      ));
      return updatedOrder;
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to update order status');
      return null;
    }
  };

  useEffect(() => {
    fetchOrders();
  }, [JSON.stringify(filters)]);

  return {
    orders,
    loading,
    error,
    refetch: fetchOrders,
    createOrder,
    updateOrderStatus,
  };
};

export const useSalesOrder = (id: string | null) => {
  const [order, setOrder] = useState<SalesOrder | null>(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    if (!id) {
      setOrder(null);
      return;
    }

    const fetchOrder = async () => {
      setLoading(true);
      setError(null);
      try {
        const data = await salesService.getById(id);
        setOrder(data);
      } catch (err) {
        setError(err instanceof Error ? err.message : 'Failed to fetch sales order');
      } finally {
        setLoading(false);
      }
    };

    fetchOrder();
  }, [id]);

  const updateStatus = async (status: SalesOrderStatus): Promise<SalesOrder | null> => {
    if (!id) return null;
    
    setError(null);
    try {
      const updatedOrder = await salesService.updateStatus(id, status);
      setOrder(updatedOrder);
      return updatedOrder;
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to update order status');
      return null;
    }
  };

  return { order, loading, error, updateStatus };
};

export const useSalesDashboard = (range: SalesDashboardRange) => {
  return useQuery({
    queryKey: ['salesOrders', 'dashboard', range],
    queryFn: () => salesService.getDashboard(range),
    placeholderData: keepPreviousData,
  });
};
